import React, { useMemo, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  DEFAULT_DEVICE_PLATFORMS,
  DEVICE_PLATFORM_LABELS,
  FACEBOOK_POSITION_LABELS,
  getDefaultFacebookPlacementState,
  type FacebookPlacementMode,
  type FacebookPlacementState,
  INSTAGRAM_POSITION_LABELS,
  META_STYLE_PLACEMENT_GROUPS,
  normalizeFacebookPlacementState,
  PUBLISHER_PLATFORM_OPTIONS,
  PUBLISHER_PLATFORM_LABELS,
  type PlacementGroupItem,
  USER_OS_LABELS,
} from '../pages/facebookPlacements';

type Props = {
  mode?: FacebookPlacementMode;
  onModeChange?: (mode: FacebookPlacementMode) => void;
  state: FacebookPlacementState;
  onChange: (next: FacebookPlacementState) => void;
  disabled?: boolean;
  showModeToggle?: boolean;
};

const PANEL_BORDER = '1px solid var(--border)';
const PANEL_BG = 'var(--panel, #fff)';

const previewTextByGroup: Record<string, { title: string; body: string }> = {
  feeds: {
    title: '动态信息流',
    body: '建议优先保留动态消息、Instagram 动态与探索首页，这些位置和 Meta 后台的常用默认项最接近。',
  },
  'stories-reels': {
    title: '快拍、Reels',
    body: '更适合 9:16 竖版素材；如果启用了快拍或 Reels，建议素材本身也按全屏场景准备。',
  },
  'search-surface': {
    title: '搜索与发现',
    body: '这一组适合承接明确意图流量；当前系统这里只保留搜索类入口。',
  },
  'apps-sites': {
    title: '应用和网站',
    body: 'Audience Network 和 Messenger 当前在系统里按平台维度保存，不再细分成更小版位。',
  },
};

function isItemSelected(item: PlacementGroupItem, state: FacebookPlacementState) {
  switch (item.selection.kind) {
    case 'platform':
      return state.publisherPlatforms.includes(item.selection.value);
    case 'facebook_position':
      return state.facebookPositions.includes(item.selection.value);
    case 'instagram_position':
      return state.instagramPositions.includes(item.selection.value);
    case 'device_platform':
      return state.devicePlatforms.includes(item.selection.value);
    case 'user_os':
      return state.userOS.includes(item.selection.value);
    default:
      return false;
  }
}

function getItemDescription(item: PlacementGroupItem) {
  switch (item.selection.kind) {
    case 'platform':
      return `平台：${PUBLISHER_PLATFORM_LABELS[item.selection.value] ?? item.label}`;
    case 'facebook_position':
      return `Facebook 版位：${FACEBOOK_POSITION_LABELS[item.selection.value] ?? item.label}`;
    case 'instagram_position':
      return `Instagram 版位：${INSTAGRAM_POSITION_LABELS[item.selection.value] ?? item.label}`;
    case 'device_platform':
      return `设备：${DEVICE_PLATFORM_LABELS[item.selection.value] ?? item.label}`;
    case 'user_os':
      return `操作系统：${USER_OS_LABELS[item.selection.value] ?? item.label}`;
    default:
      return item.note ?? '当前系统暂不支持保存该版位。';
  }
}

export const FacebookPlacementSelector: React.FC<Props> = ({
  mode = 'manual',
  onModeChange,
  state,
  onChange,
  disabled = false,
  showModeToggle = true,
}) => {
  const isMobile = useIsMobile();
  const normalizedState = useMemo(() => normalizeFacebookPlacementState(state), [state]);
  const [showAdvancedDeviceOptions, setShowAdvancedDeviceOptions] = useState(false);
  const [placementPanelCollapsed, setPlacementPanelCollapsed] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    feeds: true,
    'stories-reels': true,
    'search-surface': true,
    'apps-sites': true,
  });
  const [activePreviewKey, setActivePreviewKey] = useState<string>('feeds');

  const manualDisabled = disabled || mode === 'automatic';
  const selectedPlacementCount = normalizedState.facebookPositions.length + normalizedState.instagramPositions.length;
  const mobileDeviceSummary = normalizedState.devicePlatforms.length === DEFAULT_DEVICE_PLATFORMS.length
    ? '所有设备'
    : normalizedState.devicePlatforms.length > 0
      ? normalizedState.devicePlatforms.map((value) => DEVICE_PLATFORM_LABELS[value] ?? value).join('、')
      : '未限制';
  const userOSSelection: 'all' | 'android' | 'ios' =
    normalizedState.userOS.includes('Android')
      ? 'android'
      : normalizedState.userOS.includes('iOS')
        ? 'ios'
        : 'all';
  const userOSSummary = userOSSelection === 'android'
    ? '仅限 Android 设备'
    : userOSSelection === 'ios'
      ? '仅限 iOS 设备'
      : '所有移动设备';

  const apply = (updater: (current: FacebookPlacementState) => FacebookPlacementState) => {
    onChange(normalizeFacebookPlacementState(updater(normalizedState)));
  };

  const togglePlatform = (platform: string) => {
    apply((current) => {
      const has = current.publisherPlatforms.includes(platform);
      const publisherPlatforms = has
        ? current.publisherPlatforms.filter((value) => value !== platform)
        : [...current.publisherPlatforms, platform];
      return {
        ...current,
        publisherPlatforms,
        facebookPositions: platform === 'facebook' && has ? [] : current.facebookPositions,
        instagramPositions: platform === 'instagram' && has ? [] : current.instagramPositions,
      };
    });
  };

  const toggleGroupItem = (item: PlacementGroupItem) => {
    if (item.disabled || item.selection.kind === 'unsupported') return;
    setActivePreviewKey(META_STYLE_PLACEMENT_GROUPS.find((group) => group.items.some((entry) => entry.id === item.id))?.id ?? 'feeds');
    apply((current) => {
      switch (item.selection.kind) {
        case 'platform':
          return current.publisherPlatforms.includes(item.selection.value)
            ? {
                ...current,
                publisherPlatforms: current.publisherPlatforms.filter((value) => value !== item.selection.value),
              }
            : {
                ...current,
                publisherPlatforms: [...current.publisherPlatforms, item.selection.value],
              };
        case 'facebook_position':
          return current.facebookPositions.includes(item.selection.value)
            ? {
                ...current,
                facebookPositions: current.facebookPositions.filter((value) => value !== item.selection.value),
              }
            : {
                ...current,
                publisherPlatforms: current.publisherPlatforms.includes('facebook') ? current.publisherPlatforms : [...current.publisherPlatforms, 'facebook'],
                facebookPositions: [...current.facebookPositions, item.selection.value],
              };
        case 'instagram_position':
          return current.instagramPositions.includes(item.selection.value)
            ? {
                ...current,
                instagramPositions: current.instagramPositions.filter((value) => value !== item.selection.value),
              }
            : {
                ...current,
                publisherPlatforms: current.publisherPlatforms.includes('instagram') ? current.publisherPlatforms : [...current.publisherPlatforms, 'instagram'],
                instagramPositions: [...current.instagramPositions, item.selection.value],
              };
        case 'device_platform':
          return current.devicePlatforms.includes(item.selection.value)
            ? {
                ...current,
                devicePlatforms: current.devicePlatforms.filter((value) => value !== item.selection.value),
              }
            : {
                ...current,
                devicePlatforms: [...current.devicePlatforms, item.selection.value],
              };
        case 'user_os':
          return current.userOS.includes(item.selection.value)
            ? {
                ...current,
                userOS: current.userOS.filter((value) => value !== item.selection.value),
              }
            : {
                ...current,
                userOS: [...current.userOS, item.selection.value],
              };
        default:
          return current;
      }
    });
  };

  const toggleGroup = (groupId: string) => {
    const group = META_STYLE_PLACEMENT_GROUPS.find((entry) => entry.id === groupId);
    if (!group) return;
    const supportedItems = group.items.filter((item) => !item.disabled && item.selection.kind !== 'unsupported');
    const allSelected = supportedItems.length > 0 && supportedItems.every((item) => isItemSelected(item, normalizedState));

    apply((current) => {
      let next = current;
      for (const item of supportedItems) {
        const selected = isItemSelected(item, next);
        if ((allSelected && selected) || (!allSelected && !selected)) {
          next = normalizeFacebookPlacementState(
            (() => {
              switch (item.selection.kind) {
                case 'platform':
                  return {
                    ...next,
                    publisherPlatforms: allSelected
                      ? next.publisherPlatforms.filter((value) => value !== item.selection.value)
                      : [...next.publisherPlatforms, item.selection.value],
                  };
                case 'facebook_position':
                  return allSelected
                    ? {
                        ...next,
                        facebookPositions: next.facebookPositions.filter((value) => value !== item.selection.value),
                      }
                    : {
                        ...next,
                        publisherPlatforms: next.publisherPlatforms.includes('facebook') ? next.publisherPlatforms : [...next.publisherPlatforms, 'facebook'],
                        facebookPositions: [...next.facebookPositions, item.selection.value],
                      };
                case 'instagram_position':
                  return allSelected
                    ? {
                        ...next,
                        instagramPositions: next.instagramPositions.filter((value) => value !== item.selection.value),
                      }
                    : {
                        ...next,
                        publisherPlatforms: next.publisherPlatforms.includes('instagram') ? next.publisherPlatforms : [...next.publisherPlatforms, 'instagram'],
                        instagramPositions: [...next.instagramPositions, item.selection.value],
                      };
                case 'device_platform':
                  return {
                    ...next,
                    devicePlatforms: allSelected
                      ? next.devicePlatforms.filter((value) => value !== item.selection.value)
                      : [...next.devicePlatforms, item.selection.value],
                  };
                case 'user_os':
                  return {
                    ...next,
                    userOS: allSelected
                      ? next.userOS.filter((value) => value !== item.selection.value)
                      : [...next.userOS, item.selection.value],
                  };
                default:
                  return next;
              }
            })(),
          );
        }
      }
      return next;
    });
    setActivePreviewKey(groupId);
  };

  const activePreview = previewTextByGroup[activePreviewKey] ?? previewTextByGroup.feeds;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {showModeToggle && onModeChange && (
        <div style={{ border: PANEL_BORDER, borderRadius: 14, padding: 14, background: PANEL_BG }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}>
              <input
                type="radio"
                name="fb-placement-mode"
                checked={mode === 'automatic'}
                onChange={() => {
                  if (disabled) return;
                  onModeChange('automatic');
                }}
                disabled={disabled}
                style={{ accentColor: 'var(--primary)' }}
              />
              自动版位（Meta 推荐）
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}>
              <input
                type="radio"
                name="fb-placement-mode"
                checked={mode === 'manual'}
                onChange={() => {
                  if (disabled) return;
                  onModeChange('manual');
                  if (
                    normalizedState.publisherPlatforms.length === 0 &&
                    normalizedState.facebookPositions.length === 0 &&
                    normalizedState.instagramPositions.length === 0 &&
                    normalizedState.devicePlatforms.length === 0 &&
                    normalizedState.userOS.length === 0
                  ) {
                    onChange(getDefaultFacebookPlacementState());
                  }
                }}
                disabled={disabled}
                style={{ accentColor: 'var(--primary)' }}
              />
              手动选择版位
            </label>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            {mode === 'automatic'
              ? '保存时不写入具体版位定向字段，交给 Meta 自动分发。'
              : '手动模式下会按平台、版位、设备与操作系统分别保存。'}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.3fr) minmax(280px, .8fr)', gap: 14 }}>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ border: PANEL_BORDER, borderRadius: 14, padding: 14, background: PANEL_BG, opacity: manualDisabled ? 0.6 : 1 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>设备</div>
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text3)' }}>{mobileDeviceSummary}</div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
              {DEFAULT_DEVICE_PLATFORMS.map((item) => {
                const checked = normalizedState.devicePlatforms.includes(item);
                return (
                  <label
                    key={item}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      borderRadius: 10,
                      border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
                      background: checked ? 'rgba(18,16,184,0.06)' : 'var(--bg2)',
                      cursor: manualDisabled ? 'not-allowed' : 'pointer',
                      minWidth: 108,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleGroupItem({ id: `device-${item}`, label: DEVICE_PLATFORM_LABELS[item] ?? item, selection: { kind: 'device_platform', value: item } })}
                      disabled={manualDisabled}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <span>{DEVICE_PLATFORM_LABELS[item] ?? item}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ border: PANEL_BORDER, borderRadius: 14, padding: 14, background: PANEL_BG, opacity: manualDisabled ? 0.6 : 1 }}>
            <button
              type="button"
              onClick={() => setShowAdvancedDeviceOptions((prev) => !prev)}
              style={{ background: 'transparent', border: 0, padding: 0, color: 'var(--primary)', fontSize: 13, fontWeight: 600, cursor: manualDisabled ? 'not-allowed' : 'pointer' }}
              disabled={manualDisabled}
            >
              {showAdvancedDeviceOptions ? '隐藏选项 ▲' : '隐藏选项 ▼'}
            </button>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>指定移动设备和操作系统</div>
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text3)' }}>{userOSSummary}</div>
            </div>
            {showAdvancedDeviceOptions && (
              <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                {[
                  { value: 'all', label: '所有移动设备' },
                  { value: 'android', label: '仅限 Android 设备' },
                  { value: 'ios', label: '仅限 iOS 设备' },
                  { value: 'other', label: '仅限非智能手机', disabled: true },
                ].map((option) => {
                  const checked = userOSSelection === option.value;
                  const optionDisabled = manualDisabled || !!option.disabled;
                  return (
                    <label
                      key={option.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
                        background: checked ? 'rgba(18,16,184,0.08)' : 'var(--bg2)',
                        cursor: optionDisabled ? 'not-allowed' : 'pointer',
                        opacity: optionDisabled ? 0.55 : 1,
                      }}
                    >
                      <input
                        type="radio"
                        name="fb-mobile-os"
                        checked={checked}
                        disabled={optionDisabled}
                        onChange={() => {
                          if (optionDisabled) return;
                          apply((current) => ({
                            ...current,
                            userOS: option.value === 'android' ? ['Android'] : option.value === 'ios' ? ['iOS'] : [],
                            devicePlatforms: current.devicePlatforms.includes('mobile') ? current.devicePlatforms : [...current.devicePlatforms, 'mobile'],
                          }));
                        }}
                        style={{ accentColor: 'var(--primary)' }}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>这里的操作系统筛选独立于上方“设备”选择；选择 Android 或 iOS 时，会自动保留“移动设备”。</div>
              </div>
            )}
          </div>

          <div style={{ border: PANEL_BORDER, borderRadius: 14, padding: 14, background: PANEL_BG, opacity: manualDisabled ? 0.6 : 1 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>平台</div>
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text3)' }}>和 Meta 后台保持同样的“先选平台，再细化版位”逻辑。</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
              {PUBLISHER_PLATFORM_OPTIONS.map((option) => {
                const checked = normalizedState.publisherPlatforms.includes(option.value);
                const builtinDisabled = 'disabled' in option && !!option.disabled;
                const optionDisabled = manualDisabled || builtinDisabled;
                return (
                  <label
                    key={option.value}
                    title={builtinDisabled ? '当前系统暂不支持单独保存该平台版位' : option.label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
                      background: checked ? 'rgba(18,16,184,0.06)' : 'var(--bg2)',
                      cursor: optionDisabled ? 'not-allowed' : 'pointer',
                      opacity: optionDisabled ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePlatform(option.value)}
                      disabled={optionDisabled}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ border: PANEL_BORDER, borderRadius: 14, padding: 14, background: PANEL_BG, opacity: manualDisabled ? 0.6 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>版位</div>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text3)' }}>分组方式尽量贴近 Meta 后台，未接入的字段会明确置灰。</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setPlacementPanelCollapsed((prev) => !prev)}
                  style={{ background: 'transparent', border: 0, padding: 0, color: 'var(--primary)', fontSize: 12, cursor: 'pointer' }}
                >
                  {placementPanelCollapsed ? '展开版位' : '收起版位'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={manualDisabled}
                  onClick={() => onChange(getDefaultFacebookPlacementState())}
                >
                  恢复默认
                </button>
              </div>
            </div>

            {placementPanelCollapsed ? (
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                已选 {selectedPlacementCount} 个 Facebook / Instagram 版位
              </div>
            ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {META_STYLE_PLACEMENT_GROUPS.map((group) => {
                const supportedItems = group.items.filter((item) => !item.disabled && item.selection.kind !== 'unsupported');
                const selectedCount = supportedItems.filter((item) => isItemSelected(item, normalizedState)).length;
                const allSelected = supportedItems.length > 0 && selectedCount === supportedItems.length;
                const partiallySelected = selectedCount > 0 && !allSelected;
                const expanded = expandedGroups[group.id] ?? true;

                return (
                  <div key={group.id} style={{ border: PANEL_BORDER, borderRadius: 12, overflow: 'hidden' }}>
                    <div
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px', background: 'var(--bg2)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedGroups((prev) => ({ ...prev, [group.id]: !expanded }));
                          setActivePreviewKey(group.id);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 0, padding: 0, cursor: 'pointer', color: 'inherit', font: 'inherit' }}
                      >
                        <span style={{ display: 'inline-block', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .2s ease' }}>▾</span>
                        <span style={{ fontWeight: 700 }}>{group.label}</span>
                      </button>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: manualDisabled ? 'not-allowed' : 'pointer', fontSize: 12, color: 'var(--text2)' }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(node) => {
                            if (node) node.indeterminate = partiallySelected;
                          }}
                          onChange={() => toggleGroup(group.id)}
                          disabled={manualDisabled}
                          style={{ accentColor: 'var(--primary)' }}
                        />
                        全选此组
                      </label>
                    </div>
                    {expanded && (
                      <div style={{ padding: 12, display: 'grid', gap: 8 }}>
                        {group.description && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{group.description}</div>}
                        {group.items.map((item) => {
                          const checked = isItemSelected(item, normalizedState);
                          const itemDisabled = manualDisabled || item.disabled || item.selection.kind === 'unsupported';
                          return (
                            <label
                              key={item.id}
                              title={item.note ?? item.label}
                              onMouseEnter={() => setActivePreviewKey(group.id)}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 10,
                                padding: '8px 10px',
                                borderRadius: 10,
                                border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
                                background: checked ? 'rgba(18,16,184,0.06)' : '#fff',
                                cursor: itemDisabled ? 'not-allowed' : 'pointer',
                                opacity: itemDisabled ? 0.55 : 1,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleGroupItem(item)}
                                disabled={itemDisabled}
                                style={{ accentColor: 'var(--primary)', marginTop: 2 }}
                              />
                              <span style={{ display: 'grid', gap: 4 }}>
                                <span style={{ fontSize: 14, color: 'var(--text)' }}>{item.label}</span>
                                <span style={{ fontSize: 12, color: 'var(--text3)' }}>
                                  {item.note ?? getItemDescription(item)}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </div>

        <aside style={{ border: PANEL_BORDER, borderRadius: 14, padding: 16, background: PANEL_BG, alignSelf: 'start', position: isMobile ? 'static' : 'sticky', top: isMobile ? undefined : 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 8 }}>版位摘要</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', marginBottom: 8 }}>{activePreview.title}</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text2)', marginBottom: 14 }}>{activePreview.body}</div>

          <div style={{ display: 'grid', gap: 10, paddingTop: 12, borderTop: PANEL_BORDER }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>已选平台</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {normalizedState.publisherPlatforms.length > 0
                  ? normalizedState.publisherPlatforms.map((value) => (
                      <span key={value} style={{ padding: '4px 8px', borderRadius: 999, background: 'rgba(18,16,184,0.08)', color: 'var(--primary)', fontSize: 12, fontWeight: 700 }}>
                        {PUBLISHER_PLATFORM_LABELS[value] ?? value}
                      </span>
                    ))
                  : <span style={{ fontSize: 12, color: 'var(--text3)' }}>自动版位下不限定平台</span>}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
              <div style={{ padding: 10, borderRadius: 12, background: 'var(--bg2)' }}>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Facebook / Instagram 版位</div>
                <div style={{ marginTop: 4, fontSize: 18, fontWeight: 800 }}>{selectedPlacementCount}</div>
              </div>
              <div style={{ padding: 10, borderRadius: 12, background: 'var(--bg2)' }}>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>设备 / OS 过滤</div>
                <div style={{ marginTop: 4, fontSize: 18, fontWeight: 800 }}>
                  {normalizedState.devicePlatforms.length + normalizedState.userOS.length}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
              置灰选项用于提示 Meta 后台常见入口，但当前系统后端还没有对应的独立保存字段，避免前端勾了却落不到请求里。
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};
